/**
 * Subscription plans: what each level costs and what it unlocks, for members and for businesses.
 *
 * This is where plans are owned now. The subscription plan seeder used to overwrite them on every
 * deploy with a migration; it only inserts missing plans since this page was added.
 *
 * Putting ONE user on a plan is a different job, done from that user's Subscription tab.
 */

import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { Add, DeleteOutline, Edit } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageShell } from '@components/common/PageShell'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { ResponsiveTabs, type ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { plansApi } from '@api/endpoints/plans'
import type { AdminSubscriptionPlanDto } from '@api/types/plans'
import { PlanEditorDialog } from '../components/plans/PlanEditorDialog'
import {
  type AnyPlan,
  type PlanKindSpec,
  BUSINESS_SPEC,
  CONSUMER_SPEC,
  deleteBlocker,
  enabledFeatureLabels,
  formatBytes,
  formatMoney,
  planErrorMessage,
  planQueryKey,
} from '../components/plans/planSpecs'

const TABS: ResponsiveTabItem[] = [
  { key: 'consumer', label: 'Consumer' },
  { key: 'business', label: 'Business' },
]

interface EditorState {
  spec: PlanKindSpec
  plan: AnyPlan | null
}

export default function AdminPlansPage() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('consumer')
  const spec = tab === 'business' ? BUSINESS_SPEC : CONSUMER_SPEC

  const consumer = useQuery({
    queryKey: planQueryKey('consumer'),
    queryFn: plansApi.consumer.list,
    enabled: spec.kind === 'consumer',
  })
  const business = useQuery({
    queryKey: planQueryKey('business'),
    queryFn: plansApi.business.list,
    enabled: spec.kind === 'business',
  })
  const query = spec.kind === 'consumer' ? consumer : business
  const rows: AnyPlan[] = query.data ?? []

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<EditorState & { plan: AnyPlan } | null>(null)

  const remove = useMutation({
    mutationFn: (target: { spec: PlanKindSpec; plan: AnyPlan }) =>
      target.spec.kind === 'consumer'
        ? plansApi.consumer.remove(target.plan.id)
        : plansApi.business.remove(target.plan.id),
    onSuccess: (_data, target) => {
      setDeleteTarget(null)
      queryClient.invalidateQueries({ queryKey: planQueryKey(target.spec.kind) })
    },
  })

  const nextSortOrder = rows.reduce((max, p) => Math.max(max, p.sortOrder + 1), 0)

  const changeTab = (key: string) => {
    setTab(key)
    setPage(0)
  }

  const openDelete = (plan: AnyPlan) => {
    remove.reset()
    setDeleteTarget({ spec, plan })
  }

  const columns = useMemo<DataTableColumn<AnyPlan>[]>(() => {
    const list: DataTableColumn<AnyPlan>[] = [
      {
        key: 'plan',
        header: 'Plan',
        primary: true,
        render: (plan) => (
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography component="span" sx={{ fontWeight: 600 }}>
                {plan.displayName}
              </Typography>
              {plan.isFreeTier && <Chip size="small" color="success" label="Free plan" />}
              {!plan.isActive && <Chip size="small" variant="outlined" label="Inactive" />}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {plan.name}
            </Typography>
          </Box>
        ),
      },
      { key: 'price', header: 'Price', render: (plan) => <PriceLabel plan={plan} /> },
      {
        key: 'trial',
        header: 'Trial',
        hideBelow: 'md',
        render: (plan) => (plan.trialPeriodDays > 0 ? `${plan.trialPeriodDays} days` : '—'),
      },
      { key: 'storage', header: 'Storage', hideBelow: 'lg', render: (plan) => formatBytes(plan.maxStorageSize) },
      {
        key: 'features',
        header: 'Features',
        hideBelow: 'md',
        render: (plan) => {
          const labels = enabledFeatureLabels(plan, spec)
          if (labels.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            )
          }
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {labels.map((label) => (
                <Chip key={label} size="small" variant="outlined" label={label} />
              ))}
            </Stack>
          )
        },
      },
    ]

    if (spec.kind === 'consumer') {
      list.push({
        key: 'subscribers',
        header: 'Subscribers',
        align: 'right',
        render: (plan) => (plan as AdminSubscriptionPlanDto).subscriberCount.toLocaleString(),
      })
    }

    list.push({ key: 'order', header: 'Order', align: 'right', hideBelow: 'lg', render: (plan) => plan.sortOrder })
    return list
  }, [spec])

  return (
    <PageShell
      title="Plans"
      subtitle="What each subscription level costs and unlocks. Saved changes apply straight away to everyone on the plan."
      actions={
        <Button variant="contained" startIcon={<Add />} onClick={() => setEditor({ spec, plan: null })}>
          New {spec.noun}
        </Button>
      }
    >
      <ResponsiveTabs tabs={TABS} value={tab} onChange={changeTab} sx={{ mb: 2 }} />

      {spec.kind === 'consumer' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          To put one person on a plan, open them under Users and use their Subscription tab.
        </Alert>
      )}

      <DataTable<AnyPlan>
        rows={rows}
        columns={columns}
        getRowKey={(plan) => plan.id}
        loading={query.isLoading}
        error={query.error ? planErrorMessage(query.error, 'Failed to load plans') : null}
        emptyMessage={spec.kind === 'consumer' ? 'No plans yet.' : 'No business plans yet.'}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(plan) => setEditor({ spec, plan })}
        getRowActions={(plan) => {
          const blocker = deleteBlocker(plan, spec)
          return [
            { key: 'edit', label: 'Edit', icon: <Edit fontSize="small" />, onClick: () => setEditor({ spec, plan }) },
            {
              key: 'delete',
              label: 'Delete',
              icon: <DeleteOutline fontSize="small" />,
              color: 'error',
              disabled: blocker !== null,
              // Explains a disabled Delete (tooltip in the table, menu caption on a phone).
              hint: blocker ?? undefined,
              onClick: () => openDelete(plan),
            },
          ]
        }}
      />

      {editor && (
        <PlanEditorDialog
          key={`${editor.spec.kind}:${editor.plan?.id ?? 'new'}`}
          spec={editor.spec}
          plan={editor.plan}
          nextSortOrder={nextSortOrder}
          onClose={() => setEditor(null)}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Delete ${deleteTarget?.plan.displayName ?? 'this plan'}?`}
        message={
          deleteTarget
            ? `The ${deleteTarget.spec.noun} "${deleteTarget.plan.displayName}" will be removed for good. To keep it but hide it from sign-up, edit it and switch off Active instead.`
            : ''
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        {remove.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {planErrorMessage(remove.error, 'The plan could not be deleted')}
          </Alert>
        )}
      </ConfirmDialog>
    </PageShell>
  )
}

function PriceLabel({ plan }: { plan: AnyPlan }) {
  if (plan.monthlyPrice === 0 && !plan.annualPrice) {
    return <Typography variant="body2">Free</Typography>
  }
  return (
    <Box>
      <Typography variant="body2">{formatMoney(plan.monthlyPrice, plan.currency)}/mo</Typography>
      {plan.annualPrice != null && (
        <Typography variant="caption" color="text.secondary">
          {formatMoney(plan.annualPrice, plan.currency)}/yr
        </Typography>
      )}
    </Box>
  )
}
